import React, { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEdit,
  faTrash,
  faEye,
  faFilter,
  faSearch,
  faRefresh,
  faList,
  faLayerGroup,
  faBuilding,
  faUsers,
  faTag,
} from "@fortawesome/free-solid-svg-icons";
import "./dashboard.css";
import "./viewSections.css";
import Sidebar from "./Sidebar";

// Base URL for PHP backend
const BASE_URL = "https://mhsdev.online/MHS-WEB/api";
const SECTIONS_API = `${BASE_URL}/StdSectionsCrud.php`;

const ViewSections = () => {
  const [selectedClass, setSelectedClass] = useState("");
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [stats, setStats] = useState({
    totalSections: 0,
    girlsSections: 0,
    boysSections: 0,
  });

  const classList = [
    "Play Group",
    "Nursery",
    "Prep",
    "Class 1",
    "Class 2",
    "Class 3",
    "Class 4",
    "Class 5",
    "Class 6",
    "Class 7",
    "Class 8",
    "Class Pre 9",
    "Class 9",
    "Class 10",
  ];

  // Fetch sections from PHP backend
  const fetchSections = async (className) => {
    if (!className) return;

    setLoading(true);
    try {
      const response = await axios.get(SECTIONS_API, {
        params: {
          class: className,
        },
        headers: {
          "Content-Type": "application/json",
        },
      });

      console.log("API Response:", response.data); // Debug log

      if (response.data && response.data.success) {
        // Extract sections array from response.data.data
        setSections(response.data.data || []);

        // Extract and set stats from response
        if (response.data.stats) {
          setStats({
            totalSections: parseInt(response.data.stats.total_sections) || 0,
            girlsSections: parseInt(response.data.stats.girls_sections) || 0,
            boysSections: parseInt(response.data.stats.boys_sections) || 0,
          });
        }
      } else {
        console.error("API returned unsuccessful response:", response.data);
        setSections([]);
        setStats({ totalSections: 0, girlsSections: 0, boysSections: 0 });

        Swal.fire({
          icon: "error",
          title: "Error",
          text: response.data?.error || "Failed to fetch sections",
          confirmButtonColor: "#2E2A74",
        });
      }
    } catch (error) {
      console.error("Error fetching sections:", error);
      setSections([]);
      setStats({ totalSections: 0, girlsSections: 0, boysSections: 0 });

      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          error.response?.data?.error ||
          error.response?.data?.message ||
          "Failed to fetch sections. Please try again.",
        confirmButtonColor: "#2E2A74",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClassChange = (e) => {
    const className = e.target.value;
    setSelectedClass(className);
    setSearchTerm(""); // Reset search when class changes
    setSections([]); // Clear previous sections
    setStats({ totalSections: 0, girlsSections: 0, boysSections: 0 }); // Reset stats

    if (className) fetchSections(className);
  };

  // Delete section using PHP API
  const handleDelete = (id, sectionName) => {
    Swal.fire({
      title: "Delete Section",
      text: `Are you sure you want to delete "${sectionName}"? This action cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#2E8B57",
      cancelButtonColor: "#2E2A74",
      confirmButtonText: "Yes, delete it",
      cancelButtonText: "Cancel",
      reverseButtons: true,
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await axios.delete(SECTIONS_API, {
            params: {
              action: "delete-section",
              id: id,
            },
            headers: {
              "Content-Type": "application/json",
            },
          });

          if (response.data.success) {
            Swal.fire({
              title: "Deleted!",
              text:
                response.data.message ||
                "The section has been deleted successfully.",
              icon: "success",
              confirmButtonColor: "#2E8B57",
              timer: 3000,
            });
            fetchSections(selectedClass);
          } else {
            throw new Error(response.data.error || "Failed to delete section");
          }
        } catch (error) {
          console.error("Error deleting section:", error);
          Swal.fire({
            title: "Error!",
            text:
              error.response?.data?.error ||
              error.response?.data?.message ||
              "Failed to delete the section. Please try again.",
            icon: "error",
            confirmButtonColor: "#2E2A74",
          });
        }
      }
    });
  };

  // Update section using PHP API
  const handleUpdate = (section) => {
    const isSpecialClass = ["Class Pre 9", "Class 9", "Class 10"].includes(
      section.class_name
    );

    Swal.fire({
      title: "Update Section Details",
      html: `
        <div class="update-modal">
          <div class="modal-header">
            <h3 style="color:gold">Edit Section: ${section.section_name}</h3>
            <p class="modal-subtitle" style="color:white">Update the details for this section</p>
          </div>
          
          <div class="modal-body">
            <div class="form-row">
              <div class="form-group">
                <label for="sectionName">Section Name <span class="required">*</span></label>
                <input 
                  id="sectionName" 
                  class="modal-input" 
                  type="text" 
                  value="${section.section_name}"
                  placeholder="Enter section name"
                  style="width:180px"
                />
              </div>
              
              <div class="form-group">
                <label for="sectionFor">Section For <span class="required">*</span></label>
                <select id="sectionFor" class="modal-select">
                  <option value="Girls" ${
                    section.section_for === "Girls" ? "selected" : ""
                  }>Girls</option>
                  <option value="Boys" ${
                    section.section_for === "Boys" ? "selected" : ""
                  }>Boys</option>
                </select>
              </div>
            </div>
            
            ${
              isSpecialClass
                ? `
              <div class="form-group">
                <label for="department">Department <span class="required">*</span></label>
                <select id="department" class="modal-select">
                  <option value="" ${
                    !section.department ? "selected" : ""
                  } disabled>Select Department</option>
                  <option value="Computer Science" ${
                    section.department === "Computer Science" ? "selected" : ""
                  }>Computer Science</option>
                  <option value="Bio Science" ${
                    section.department === "Bio Science" ? "selected" : ""
                  }>Bio Science</option>
                  <option value="Arts" ${
                    section.department === "Arts" ? "selected" : ""
                  }>Arts</option>
                </select>
              </div>
            `
                : ""
            }
            
            <div class="form-group">
              <label for="sectionHint">Section Hint (Auto-generated)</label>
              <input 
                id="sectionHint" 
                class="modal-input readonly" 
                readonly 
                value="${section.section_hint || ""}"
              />
              <p class="form-hint">This field updates automatically based on your changes</p>
            </div>
          </div>
        </div>
      `,
      showCancelButton: true,
      confirmButtonColor: "#2E8B57",
      cancelButtonColor: "#2E2A74",
      confirmButtonText: "Update Section",
      cancelButtonText: "Cancel",
      width: "500px",
      preConfirm: async () => {
        const sectionName = document.getElementById("sectionName").value.trim();
        const sectionFor = document.getElementById("sectionFor").value;
        const department = isSpecialClass
          ? document.getElementById("department").value
          : section.department;

        if (!sectionName) {
          Swal.showValidationMessage("Section name is required");
          return false;
        }

        if (!sectionFor) {
          Swal.showValidationMessage("Please select section for Girls or Boys");
          return false;
        }

        if (isSpecialClass && !department) {
          Swal.showValidationMessage("Department is required for this class");
          return false;
        }

        const updatedData = {
          id: section.id,
          section_name: sectionName,
          section_for: sectionFor,
          department: department,
          section_hint: `${sectionName} - ${department || ""} - ${sectionFor}`,
        };

        try {
          const response = await axios.post(
            `${SECTIONS_API}?action=update-section`,
            updatedData,
            {
              headers: {
                "Content-Type": "application/json",
              },
            }
          );

          if (response.data.success) {
            return response.data;
          } else {
            Swal.showValidationMessage(
              response.data.error || response.data.message || "Update failed"
            );
            return false;
          }
        } catch (error) {
          Swal.showValidationMessage(
            error.response?.data?.error ||
              error.response?.data?.message ||
              "Update failed"
          );
          return false;
        }
      },
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire({
          title: "Updated!",
          text:
            result.value?.message ||
            "Section details have been updated successfully.",
          icon: "success",
          confirmButtonColor: "#2E8B57",
          timer: 3000,
        });
        fetchSections(selectedClass);
      }
    });
  };

  // Filter sections based on search term
  const filteredSections = sections.filter(
    (section) =>
      (section.section_name?.toLowerCase() || "").includes(
        searchTerm.toLowerCase()
      ) ||
      (section.department?.toLowerCase() || "").includes(
        searchTerm.toLowerCase()
      ) ||
      (section.section_for?.toLowerCase() || "").includes(
        searchTerm.toLowerCase()
      ) ||
      (section.section_hint?.toLowerCase() || "").includes(
        searchTerm.toLowerCase()
      )
  );

  const handleRefresh = () => {
    if (selectedClass) {
      fetchSections(selectedClass);
    }
  };

  // Get local stats from filtered sections
  const getLocalStats = () => {
    const totalSections = sections.length;
    const girlsSections = sections.filter(
      (s) => s.section_for === "Girls"
    ).length;
    const boysSections = sections.filter(
      (s) => s.section_for === "Boys"
    ).length;

    return { totalSections, girlsSections, boysSections };
  };

  // Use server stats or local stats
  const displayStats = stats.totalSections > 0 ? stats : getLocalStats();

  return (
    <div className="dashboard-wrapper">
      <Sidebar />

      <div className="main-content">
        <section className="content">
          <div className="content-header">
            <h1>Manage Sections</h1>
            <p className="subtitle">
              View, edit, and manage class sections across your institution
            </p>
          </div>

          {/* Class Selection Card */}
          <div className="selection-card">
            <div className="card-header">
              <div className="header-icon-wrapper">
                <FontAwesomeIcon
                  icon={faFilter}
                  className="card-icon"
                  style={{ color: "white" }}
                />
              </div>
              <div>
                <h3>Filter Sections</h3>
                <p className="card-subtitle">
                  Select a class to view its sections
                </p>
              </div>
            </div>

            <div className="selection-form">
              <div className="form-group">
                <label className="form-label">
                  <FontAwesomeIcon icon={faList} className="label-icon" />
                  <span>Select Class</span>
                  <span className="required">*</span>
                </label>
                <div className="select-wrapper">
                  <select
                    value={selectedClass}
                    onChange={handleClassChange}
                    className="form-select"
                    disabled={loading}
                  >
                    <option value="">-- Choose a Class --</option>
                    {classList.map((cls, index) => (
                      <option key={index} value={cls}>
                        {cls}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Search Box */}
              {sections.length > 0 && (
                <div className="search-box">
                  <div className="search-icon">
                    <FontAwesomeIcon icon={faSearch} />
                  </div>
                  <input
                    type="text"
                    placeholder="Search sections..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="search-input"
                  />
                  {searchTerm && (
                    <button
                      className="clear-search"
                      onClick={() => setSearchTerm("")}
                    >
                      Clear
                    </button>
                  )}
                </div>
              )}

              {/* Stats Display */}
              {selectedClass && sections.length > 0 && (
                <div className="stats-container">
                  <div className="stat-card">
                    <div className="stat-icon total">
                      <FontAwesomeIcon icon={faLayerGroup} />
                    </div>
                    <div className="stat-content">
                      <h4>{displayStats.totalSections}</h4>
                      <p>Total Sections</p>
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-icon girls">
                      <FontAwesomeIcon icon={faUsers} />
                    </div>
                    <div className="stat-content">
                      <h4>{displayStats.girlsSections}</h4>
                      <p>Girls Sections</p>
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-icon boys">
                      <FontAwesomeIcon icon={faBuilding} />
                    </div>
                    <div className="stat-content">
                      <h4>{displayStats.boysSections}</h4>
                      <p>Boys Sections</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Sections Table Card */}
          {selectedClass && (
            <div className="table-card">
              <div className="table-header">
                <div className="table-title">
                  <h3>
                    <FontAwesomeIcon icon={faList} />
                    <span>Sections for {selectedClass}</span>
                  </h3>
                  <p className="table-subtitle">
                    Showing {filteredSections.length} of {sections.length}{" "}
                    sections
                  </p>
                </div>
                <div className="table-actions">
                  <button
                    style={{ width: "8rem" }}
                    className="refresh-btn"
                    onClick={handleRefresh}
                    disabled={loading}
                  >
                    <FontAwesomeIcon icon={faRefresh} spin={loading} />
                    <span>{loading ? "Refreshing..." : "Refresh"}</span>
                  </button>
                </div>
              </div>

              {loading ? (
                <div className="loading-state">
                  <div className="loading-spinner"></div>
                  <p>Loading sections...</p>
                </div>
              ) : filteredSections.length > 0 ? (
                <div className="table-container">
                  <table className="sections-table">
                    <thead>
                      <tr>
                        <th className="text-center">#</th>
                        <th>Section Name</th>
                        <th>Department</th>
                        <th>Section For</th>
                        <th>Section Hint</th>
                        <th className="text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredSections.map((section, index) => (
                        <tr key={section.id}>
                          <td className="text-center serial">{index + 1}</td>
                          <td>
                            <div className="section-name">
                              <FontAwesomeIcon
                                icon={faTag}
                                className="section-icon"
                              />
                              <span>{section.section_name || "N/A"}</span>
                            </div>
                          </td>
                          <td>
                            <span
                              className={`department-tag ${
                                !section.department ? "no-department" : ""
                              }`}
                            >
                              {section.department || "Not Specified"}
                            </span>
                          </td>
                          <td>
                            <span
                              className={`section-for ${
                                section.section_for === "Girls"
                                  ? "girls"
                                  : "boys"
                              }`}
                            >
                              <FontAwesomeIcon icon={faUsers} />
                              {section.section_for || "N/A"}
                            </span>
                          </td>
                          <td className="section-hint">
                            <span>{section.section_hint || "N/A"}</span>
                          </td>
                          <td className="actions-cell">
                            <div className="action-buttons">
                              <button
                                className="btn-edit"
                                onClick={() => handleUpdate(section)}
                                title="Edit Section"
                              >
                                <FontAwesomeIcon icon={faEdit} />
                                <span>Edit</span>
                              </button>
                              <button
                                className="btn-delete"
                                onClick={() =>
                                  handleDelete(section.id, section.section_name)
                                }
                                title="Delete Section"
                              >
                                <FontAwesomeIcon icon={faTrash} />
                                <span>Delete</span>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : searchTerm ? (
                <div className="empty-state">
                  <div className="empty-icon">
                    <FontAwesomeIcon icon={faSearch} />
                  </div>
                  <h4>No matching sections found</h4>
                  <p>Try adjusting your search terms</p>
                </div>
              ) : (
                <div className="empty-state">
                  <div className="empty-icon">
                    <FontAwesomeIcon icon={faLayerGroup} />
                  </div>
                  <h4>No sections found for {selectedClass}</h4>
                  <p>This class doesn't have any sections yet</p>
                </div>
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default ViewSections;
